import { useEffect, useState } from 'react'
import Loader2 from 'lucide-react/dist/esm/icons/loader-2'
import Trash2 from 'lucide-react/dist/esm/icons/trash-2'

import { DeleteDocumentDialog } from '@/components/documents/DeleteDocumentDialog'
import { cn } from '@/lib/utils'

interface DeleteDocumentButtonProps {
  fileName: string
  onConfirmDelete: () => void
  isDeleting: boolean
  className?: string
}

/** Trash control that asks for confirmation before deleting an uploaded document. */
export function DeleteDocumentButton({
  fileName,
  onConfirmDelete,
  isDeleting,
  className,
}: DeleteDocumentButtonProps) {
  const [open, setOpen] = useState(false)
  const [deleteRequested, setDeleteRequested] = useState(false)

  const dialogDeleting = isDeleting || (deleteRequested && open)

  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen)
    if (!nextOpen) {
      setDeleteRequested(false)
    }
  }

  const handleConfirmDelete = () => {
    setDeleteRequested(true)
    onConfirmDelete()
  }

  useEffect(() => {
    if (!deleteRequested || isDeleting || !open) return
    setOpen(false)
    setDeleteRequested(false)
  }, [deleteRequested, isDeleting, open])

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={isDeleting}
        aria-label={`Delete ${fileName}`}
        className={cn(
          'shrink-0 rounded-md p-1.5 text-muted-foreground transition-all duration-150 ease-out hover:text-primary active:scale-95 disabled:opacity-50 disabled:active:scale-100',
          className,
        )}
      >
        {isDeleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
      </button>

      <DeleteDocumentDialog
        open={open}
        onOpenChange={handleOpenChange}
        fileName={fileName}
        isDeleting={dialogDeleting}
        onConfirm={handleConfirmDelete}
      />
    </>
  )
}
